import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../context/I18nContext';
import { ChevronDown, Trash2, RefreshCw, Contact } from 'lucide-react';
import { useFollowTriggerPosition } from '../../hooks/useFollowTriggerPosition';
import ConfirmModal from './ConfirmModal';
import Tooltip from './Tooltip';
import {
  DROPDOWN_MENU_PAPER,
  DROPDOWN_MENU_ITEM,
  DROPDOWN_MENU_ANIMATION,
  DROPDOWN_MENU_KEYFRAMES,
} from './dropdownMenuStyles';

export interface BulkActionsBarOption {
  value: string;
  label: string;
}

/** @deprecated Use BulkActionsBarOption */
export type BulkActionsBarStatusOption = BulkActionsBarOption;

export interface BulkActionsBarProps<T extends { id: string }> {
  selectedCount: number;
  totalCount?: number;
  selectedItems: T[];
  statusOptions?: BulkActionsBarOption[];
  onUpdateStatus?: (ids: string[], newStatus: string) => void | Promise<void>;
  contactTypeOptions?: BulkActionsBarOption[];
  onUpdateContactType?: (ids: string[], newTypeId: string) => void | Promise<void>;
  onDeleteSelected?: () => void;
  /** Clears table selection after a confirmed bulk action (not on cancel). */
  onClearSelection?: () => void;
  className?: string;
}

const BULK_BAR_FLOATING_ID = 'bulk-actions-bar-floating';
const Z_INDEX_DROPDOWN = 101;

const NEUTRAL_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-2 h-8 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 shrink-0';

const NEUTRAL_BUTTON_PAD = 'px-2.5 lg:px-3';

const DELETE_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-2 h-8 rounded-xl border border-red-200 bg-red-50 text-sm font-medium text-red-600 transition-all hover:bg-red-100 hover:border-red-300 shrink-0 px-2.5 lg:px-3';

type PendingBulkAction =
  | { kind: 'status'; option: BulkActionsBarOption }
  | { kind: 'contactType'; option: BulkActionsBarOption };

interface BulkActionMenuProps {
  menuId: string;
  ariaLabel: string;
  label: string;
  icon: React.ReactNode;
  options: BulkActionsBarOption[];
  onSelect: (value: string) => void;
}

function BulkActionMenu({ menuId, ariaLabel, label, icon, options, onSelect }: BulkActionMenuProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const computeMenuPosition = useCallback((triggerRect: DOMRect) => ({
    bottom: typeof window !== 'undefined' ? window.innerHeight - triggerRect.top + 6 : 0,
    left: triggerRect.left,
    minWidth: Math.max(triggerRect.width, 180),
  }), []);

  const menuPosition = useFollowTriggerPosition({
    open,
    triggerRef: buttonRef,
    menuRef,
    computePosition: computeMenuPosition,
  });

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || document.getElementById(menuId)?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, menuId]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const dropdownContent =
    open &&
    menuPosition &&
    createPortal(
      <>
        <style>{DROPDOWN_MENU_KEYFRAMES}</style>
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={ariaLabel}
          className={`fixed ${DROPDOWN_MENU_PAPER}`}
          style={{
            bottom: menuPosition.bottom,
            left: menuPosition.left,
            minWidth: menuPosition.minWidth,
            zIndex: Z_INDEX_DROPDOWN,
            animation: DROPDOWN_MENU_ANIMATION,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="menuitem"
              className={`w-full ${DROPDOWN_MENU_ITEM} cursor-pointer text-left`}
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </>,
      document.body
    );

  return (
    <div className="relative">
      <Tooltip content={ariaLabel} position="top" delay={400} wrapperClassName="block">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`${NEUTRAL_BUTTON_CLASS} ${NEUTRAL_BUTTON_PAD}`}
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {icon}
          <span className="hidden lg:inline">{label}</span>
          <ChevronDown className="hidden h-4 w-4 text-slate-400 lg:block" />
        </button>
      </Tooltip>
      {dropdownContent}
    </div>
  );
}

function BulkActionsBarInner<T extends { id: string }>({
  selectedCount,
  totalCount,
  selectedItems,
  statusOptions = [],
  onUpdateStatus,
  contactTypeOptions = [],
  onUpdateContactType,
  onDeleteSelected,
  onClearSelection,
  className = '',
}: BulkActionsBarProps<T>) {
  const { t } = useI18n();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingBulkAction | null>(null);
  const [updating, setUpdating] = useState(false);

  const queueAction = (action: PendingBulkAction) => {
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (updating) return;
    setConfirmOpen(false);
    setPendingAction(null);
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    const ids = selectedItems.map((item) => item.id);
    setUpdating(true);
    try {
      if (pendingAction.kind === 'status' && onUpdateStatus) {
        await onUpdateStatus(ids, pendingAction.option.value);
      } else if (pendingAction.kind === 'contactType' && onUpdateContactType) {
        await onUpdateContactType(ids, pendingAction.option.value);
      }
      setConfirmOpen(false);
      setPendingAction(null);
      onClearSelection?.();
    } finally {
      setUpdating(false);
    }
  };

  if (selectedCount === 0) return null;

  const selectionLabel =
    totalCount != null
      ? t('common.selectedOfTotal', { selected: selectedCount, total: totalCount })
      : t('common.selectedCountOnly', { count: selectedCount });

  const showStatus = statusOptions.length > 0 && onUpdateStatus;
  const showContactType = contactTypeOptions.length > 0 && onUpdateContactType;
  const showDelete = Boolean(onDeleteSelected);
  const showDivider = (showStatus || showContactType) && showDelete;

  const confirmTitle =
    pendingAction?.kind === 'contactType'
      ? t('common.changeContactType')
      : t('common.changeStatus');

  const confirmMessage =
    pendingAction?.kind === 'contactType'
      ? t('common.confirmBulkContactTypeChange', {
          count: selectedCount,
          type: pendingAction.option.label,
        })
      : pendingAction
        ? t('common.confirmBulkStatusChange', {
            count: selectedCount,
            status: pendingAction.option.label,
          })
        : '';

  const confirmText = t('common.confirm');

  const barContent = (
    <div
      id={BULK_BAR_FLOATING_ID}
      className={`pointer-events-none fixed inset-x-0 bottom-3 z-[100] px-3 sm:px-4 md:bottom-6 md:left-20 ${className}`}
      role="region"
      aria-label={selectionLabel}
    >
      <div className="pointer-events-auto mx-auto flex w-max max-w-full items-center gap-2 overflow-x-auto rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-md sm:gap-3 sm:px-4 sm:py-2.5 lg:py-3">
        <span className="shrink-0 whitespace-nowrap text-sm font-medium text-slate-700">
          <span className="lg:hidden">{t('common.selectedCountOnly', { count: selectedCount })}</span>
          <span className="hidden lg:inline">{selectionLabel}</span>
        </span>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {showStatus && (
            <BulkActionMenu
              menuId="bulk-actions-bar-status-menu"
              ariaLabel={t('common.changeStatus')}
              label={t('common.changeStatus')}
              icon={<RefreshCw className="h-4 w-4 text-slate-500" />}
              options={statusOptions}
              onSelect={(value) => {
                const option = statusOptions.find((opt) => opt.value === value);
                if (option) queueAction({ kind: 'status', option });
              }}
            />
          )}

          {showContactType && (
            <BulkActionMenu
              menuId="bulk-actions-bar-contact-type-menu"
              ariaLabel={t('common.changeContactType')}
              label={t('common.changeContactType')}
              icon={<Contact className="h-4 w-4 text-slate-500" />}
              options={contactTypeOptions}
              onSelect={(value) => {
                const option = contactTypeOptions.find((opt) => opt.value === value);
                if (option) queueAction({ kind: 'contactType', option });
              }}
            />
          )}

          {showDivider && <div className="hidden h-6 w-px bg-slate-200 sm:block" aria-hidden />}

          {showDelete && (
            <Tooltip content={t('common.delete')} position="top" delay={400} wrapperClassName="block">
              <button
                type="button"
                onClick={onDeleteSelected}
                className={DELETE_BUTTON_CLASS}
                aria-label={t('common.delete')}
              >
                <Trash2 className="h-4 w-4" />
                <span className="hidden lg:inline">{t('common.delete')}</span>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(barContent, document.body)}
      <ConfirmModal
        isOpen={confirmOpen}
        onClose={closeConfirm}
        onConfirm={confirmAction}
        title={confirmTitle}
        message={confirmMessage}
        confirmText={confirmText}
        variant="warning"
        isLoading={updating}
      />
    </>
  );
}

const BulkActionsBar = BulkActionsBarInner as <T extends { id: string }>(
  props: BulkActionsBarProps<T>
) => React.ReactElement;

export default BulkActionsBar;
